import MuiTimeline from "@material-ui/lab/Timeline";
import MuiTimelineConnector from "@material-ui/lab/TimelineConnector";
import MuiTimelineContent from "@material-ui/lab/TimelineContent";
import MuiTimelineDot from "@material-ui/lab/TimelineDot";
import MuiTimelineItem from "@material-ui/lab/TimelineItem";
import TimelineSeparator from "@material-ui/lab/TimelineSeparator";
import clsx from "clsx";
import { requestTypeTextColor } from "components/Card/RequestCard";
import { InationalTransportPoint, ItemAtPoint, ITourPoint } from "contexts/RequestQuoteContext";
import { Address, EActionType, ERequestType } from "generated/graphql";
import { useEffect, useState } from "react";
import styled from "styled-components";
import "styled-components/macro";
import DialogDetailsStop from "../Catalog/DialogDetailsStop";
import ItemsCount from "../ItemsCount";
import { getStopItemsCount } from "../utils";

export const Timeline = styled(MuiTimeline)``;

export const TimelineItem = styled(MuiTimelineItem)`
  min-height: 35px;
  &.dot {
    min-height: 24px;
  }
  :before {
    content: none;
  }
`;
export const AddressContent = styled.div`
  font-size: 12px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #707070;
`;
export const TimelineContent = styled(MuiTimelineContent)`
  font-size: 14px;
  font-weight: 900;
  color: #707070;
  padding-top: 0px;
  margin-left: -10px;
`;

export const TimelineConnector = styled(MuiTimelineConnector)`
  width: 1px;
  &.dot {
    margin-left: 12px;
  }
`;

export const TimelineDot = styled(MuiTimelineDot)`
  height: 18px;
  width: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  box-shadow: none;
  margin: 0;
  background-color: transparent;

  &.pickup.outlined {
    color: #394fbe;
    border-color: #394fbe;
    background-color: white;
  }

  &.pickup.contained {
    background-color: #394fbe;
  }

  &.delivery.outlined {
    color: #ebae23;
    border-color: #ebae23;
  }

  &.delivery.contained {
    background-color: #394fbe;
  }
  &.done {
    color: #c9c9c9;
    border-color: #c9c9c9;
  }

  &.dot {
    height: 2px;
    width: 2px;
    padding: 0;
    margin-left: 11.75px;
  }
`;

export const ContainerTimeLineItem = styled("div")`
  cursor: pointer;
`;

export const ContainerItemsCount = styled("div")`
  float: right;
  cursor: pointer;
`;
interface IRenderPoint {
  address: Address;
  action: EActionType;
  items: ItemAtPoint[];
  isLast: boolean;
  type: ERequestType;
  onShowDetails: (items: ItemAtPoint[], action: EActionType, address: Address, type: ERequestType) => void;
}

export const ActionTypeLabel = {
  DELIVERY: "D",
  PICKUP: "P",
  PICKUP_AND_DELIVERY: "P-D",
  UNKNOWN: "S",
  PICKUP_DELIVERY: "P-D",
};
export const RenderTimeLinePoint = ({ address, action, items, isLast, onShowDetails, type }: IRenderPoint) => (
  <ContainerTimeLineItem>
    <ContainerItemsCount>
      <ItemsCount
        onClick={() => onShowDetails(items, action, address, type)}
        count={getStopItemsCount(items || [], type === ERequestType.Tr)}
        color={requestTypeTextColor[type]}
      />
    </ContainerItemsCount>
    <TimelineItem>
      <TimelineSeparator
        css={`
          &.MuiTimelineSeparator-root {
            margin-top: -6px;
          }
        `}
      >
        <TimelineDot
          className={clsx(
            action === EActionType.Delivery || action === EActionType.Unknown ? "contained" : "outlined",
            {
              pickup: action === EActionType.Pickup,
              delivery: action === EActionType.Delivery || action === EActionType.Unknown,
            }
          )}
          style={{
            color: action === EActionType.Pickup && requestTypeTextColor[type],
            borderColor: requestTypeTextColor[type],
            backgroundColor:
              (action === EActionType.Delivery || action === EActionType.Unknown) && requestTypeTextColor[type],
          }}
        >
          {ActionTypeLabel[action] || "S"}
        </TimelineDot>
        {!isLast && <TimelineConnector />}
      </TimelineSeparator>
      <TimelineContent>
        <AddressContent>{address?.address}</AddressContent>
      </TimelineContent>
    </TimelineItem>
    {!isLast && (
      <TimelineItem className="dot">
        <TimelineSeparator>
          <TimelineDot
            className={clsx("dot outlined", {
              pickup: true,
            })}
            style={{ color: requestTypeTextColor[type], borderColor: requestTypeTextColor[type] }}
          />
          <TimelineConnector className="dot" />
        </TimelineSeparator>
      </TimelineItem>
    )}
  </ContainerTimeLineItem>
);

interface ITimeLineRequestPoint {
  pointsNT?: InationalTransportPoint[];
  pointsTour?: ITourPoint[];
  type: ERequestType;
}

export default function TimeLineRequestPoint({ pointsNT, pointsTour, type }: ITimeLineRequestPoint) {
  const [showDetails, setShowDetails] = useState(false);
  const [itemsDetails, setItemsDetails] = useState(null);
  const [actionDetails, setActionDetails] = useState(null);
  const [addressDetails, setAddressDetails] = useState(null);
  const [requestType, setRequestType] = useState(null);
  useEffect(() => {
    itemsDetails ? setShowDetails(true) : setShowDetails(false);
  }, [itemsDetails]);

  const onShowDetails = (items: ItemAtPoint[], action: EActionType, address: Address, type: ERequestType) => {
    setActionDetails(action);
    setAddressDetails(address);
    setItemsDetails(items);
    setRequestType(type);
  };

  const renderPointsNT = () =>
    pointsNT?.map((point, index) => {
      switch (point?.action) {
        case EActionType.Delivery:
          return (
            <RenderTimeLinePoint
              key={index}
              action={point?.action}
              items={point?.itemsToDelivered}
              address={point?.address}
              isLast={index === pointsNT?.length - 1}
              onShowDetails={onShowDetails}
              type={ERequestType.Nt}
            />
          );
        case EActionType.Pickup:
          return (
            <RenderTimeLinePoint
              key={index}
              action={point?.action}
              items={point?.itemsToPick}
              address={point?.address}
              isLast={index === pointsNT?.length - 1}
              onShowDetails={onShowDetails}
              type={ERequestType.Nt}
            />
          );
        case EActionType.PickupDelivery:
          return (
            <>
              <RenderTimeLinePoint
                action={EActionType.Pickup}
                address={point?.address}
                isLast={false}
                onShowDetails={onShowDetails}
                items={point?.itemsToPick}
                type={ERequestType.Nt}
              />
              <RenderTimeLinePoint
                action={EActionType.Delivery}
                address={point?.address}
                isLast={index === pointsNT?.length - 1}
                onShowDetails={onShowDetails}
                items={point?.itemsToDelivered}
                type={ERequestType.Nt}
              />
            </>
          );
        default:
          break;
      }
    });

  const renderPointsTour = () =>
    pointsTour?.map((point, index) => (
      <>
        {point?.action === EActionType.Pickup ? (
          <RenderTimeLinePoint
            key={index}
            action={EActionType.Pickup}
            address={point?.address}
            isLast={index === pointsTour?.length - 1}
            onShowDetails={onShowDetails}
            items={point?.itemsToPick || []}
            type={ERequestType.Tr}
          />
        ) : (
          <RenderTimeLinePoint
            key={`${index}-`}
            action={point?.action || EActionType.Unknown}
            address={point?.address}
            isLast={index === pointsTour?.length - 1}
            onShowDetails={onShowDetails}
            items={(point?.itemsToDelivered || []).concat(point?.itemsToPick || [])}
            type={ERequestType.Tr}
          />
        )}
      </>
    ));

  return (
    <Timeline align="left">
      {type === ERequestType.Nt && renderPointsNT()}
      {type === ERequestType.Tr && renderPointsTour()}
      {showDetails && (
        <DialogDetailsStop
          open={showDetails}
          onClose={() => setItemsDetails(null)}
          action={actionDetails}
          items={itemsDetails}
          address={addressDetails}
          type={requestType}
        />
      )}
    </Timeline>
  );
}
